/**
* ─────────────────────────────────────────────────────────────────────
* FILE: src/pages/IntelTelemetry.tsx
* DESCRIPTION: Live telemetry dashboard for real-time intelligence on
*              usage, user behavior, trends by regions globally.
*              UPDATED: Integrated Admin Password authentication modal
*              as a fallback for non-licensed users, matching Analytics.
*
* @copyright 2025-2026 Anou. All rights reserved.
* @author Anou
* @version 1.5.4
* @created 2026-03-27
* @updated 2026-06-18 - Replaced static License Required modal with 
*                       interactive Admin Login modal. Users can now 
*                       authenticate with admin credentials if they lack 
*                       a Business/Enterprise license.
* ─────────────────────────────────────────────────────────────────────
*/
import { useState, useEffect, useRef } from 'react';
import { useUser } from '@clerk/react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import {
Activity, Globe, TrendingUp, AlertTriangle, RefreshCw, Users, Clock, Server,
CheckCircle, Tag, Zap, Shield, LogOut, Palette, Video, Film, Wand2, Box,
BarChart3, PieChart as PieChartIcon, Upload, Lock, Sparkles, Cpu,
ChevronDown, ChevronUp, ArrowUpDown, MessageCircle, Camera, Music, Share2,
Eye, EyeOff
} from 'lucide-react';
import {
LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
// 🌍 MAP IMPORTS
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import worldData from '../data/world-110m.json';

// ─────────────────────────────────────────────────────────────────────
// ADMIN CREDENTIALS (Fallback for non-licensed users)
// ─────────────────────────────────────────────────────────────────────
const ADMIN_CREDENTIALS = {
  email: 'admin@verimark.ai',
  password: 'admin2026',
};

// ─────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────
interface TelemetryStats {
  ProcessedMedia: number;
  maxProcessingTime: number;
  avgProcessingTime: number;
  riskAlerts: number;
}
interface GeoData {
  country: string;
  uploads: number;
  riskLevel: 'low' | 'medium' | 'high';
  avgTrustScore: number;
  aiGenerated: number;
  humanCreated: number;
  coordinates: { lat: number; lng: number };
}
interface CreatorTool {
  rank: number;
  tool: string;
  uploadCount: number;
  avgTrust: number;
  riskPercent: number;
  trend: number[];
  icon?: React.ElementType;
}
interface ContentType {
  name: string;
  value: number;
  color: string;
}
interface PlatformOrigin {
  platform: string;
  count: number;
  percentage: number;
  icon: React.ElementType;
  color: string;
}
interface CountryOrigin {
  country: string;
  percentage: number;
}
interface ActivityEvent {
  id: string;
  timestamp: string;
  type: 'upload' | 'alert' | 'tag' | 'milestone';
  description: string;
  riskLevel?: 'low' | 'medium' | 'high';
  countryCode?: string;
}
interface TagAdoption {
  tag: string;
  count: number;
  growthRate: number;
  suggestedCategory: string;
}
interface ProviderStatus {
  name: string;
  responseTime: number;
  successRate: number;
  errors: number;
  status: 'operational' | 'degraded' | 'down';
}
type TimeRange = '5m' | '15m' | '1h' | '24h';
type RegionFilter = 'global' | 'region';
type ActiveTab = 'commandMap' | 'content' | 'behavior' | 'system';
type ContentSubTab = 'tools' | 'distribution' | 'platform';
type BehaviorSubTab = 'patterns' | 'countryOrigin' | 'tags';

// ─────────────────────────────────────────────────────────────────────
// THEME COLORS
// ─────────────────────────────────────────────────────────────────────
const THEME = {
  blue: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
  emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400' },
  purple: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400' },
  amber: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400' },
};

// ────────────────────────────────────────────────────────────────────
// MOCK DATA
// ─────────────────────────────────────────────────────────────────────
const MOCK_STATS: TelemetryStats = { ProcessedMedia: 3523, maxProcessingTime: 342, avgProcessingTime: 2.3, riskAlerts: 23 };
const MOCK_GEO_DATA: GeoData[] = [
  { country: 'United States', uploads: 450, riskLevel: 'low', avgTrustScore: 0.92, aiGenerated: 65, humanCreated: 35, coordinates: { lat: 37.0902, lng: -95.7129 } },
  { country: 'United Kingdom', uploads: 280, riskLevel: 'low', avgTrustScore: 0.89, aiGenerated: 45, humanCreated: 55, coordinates: { lat: 55.3781, lng: -3.4360 } },
  { country: 'Germany', uploads: 220, riskLevel: 'low', avgTrustScore: 0.91, aiGenerated: 50, humanCreated: 50, coordinates: { lat: 51.1657, lng: 10.4515 } },
  { country: 'China', uploads: 380, riskLevel: 'medium', avgTrustScore: 0.78, aiGenerated: 85, humanCreated: 15, coordinates: { lat: 35.8617, lng: 104.1954 } },
  { country: 'India', uploads: 320, riskLevel: 'medium', avgTrustScore: 0.82, aiGenerated: 40, humanCreated: 60, coordinates: { lat: 20.5937, lng: 78.9629 } },
  { country: 'Brazil', uploads: 180, riskLevel: 'low', avgTrustScore: 0.87, aiGenerated: 55, humanCreated: 45, coordinates: { lat: -14.2350, lng: -51.9253 } },
  { country: 'Australia', uploads: 150, riskLevel: 'low', avgTrustScore: 0.93, aiGenerated: 48, humanCreated: 52, coordinates: { lat: -25.2744, lng: 133.7751 } },
  { country: 'Japan', uploads: 260, riskLevel: 'low', avgTrustScore: 0.94, aiGenerated: 60, humanCreated: 40, coordinates: { lat: 36.2048, lng: 138.2529 } },
  { country: 'Russia', uploads: 190, riskLevel: 'high', avgTrustScore: 0.65, aiGenerated: 75, humanCreated: 25, coordinates: { lat: 61.5240, lng: 105.3188 } },
  { country: 'South Africa', uploads: 95, riskLevel: 'medium', avgTrustScore: 0.79, aiGenerated: 30, humanCreated: 70, coordinates: { lat: -30.5595, lng: 22.9375 } },
];
const MOCK_CREATOR_TOOLS: CreatorTool[] = [
  { rank: 1, tool: 'Midjourney', uploadCount: 3300, avgTrust: 0.92, riskPercent: 15, trend: [65, 72, 68, 75, 82, 79, 85], icon: Sparkles },
  { rank: 2, tool: 'Photoshop', uploadCount: 474, avgTrust: 0.95, riskPercent: 26, trend: [45, 48, 52, 49, 55, 58, 62], icon: Palette },
  { rank: 3, tool: 'FFmpeg', uploadCount: 358, avgTrust: 0.92, riskPercent: 13, trend: [32, 35, 38, 36, 40, 42, 45], icon: Video },
  { rank: 4, tool: 'Final Cut', uploadCount: 339, avgTrust: 0.92, riskPercent: 19, trend: [28, 30, 33, 31, 35, 37, 40], icon: Film },
  { rank: 5, tool: 'Runway', uploadCount: 163, avgTrust: 0.86, riskPercent: 15, trend: [15, 18, 20, 19, 22, 24, 26], icon: Wand2 },
  { rank: 6, tool: 'DALL-E', uploadCount: 137, avgTrust: 0.95, riskPercent: 19, trend: [12, 14, 16, 15, 18, 20, 22], icon: Sparkles },
  { rank: 7, tool: 'Stable Diffusion', uploadCount: 298, avgTrust: 0.88, riskPercent: 22, trend: [25, 28, 32, 30, 35, 38, 42], icon: Box },
];
const MOCK_CONTENT_TYPES: ContentType[] = [
  { name: 'DeepFake', value: 18, color: '#3b82f6' }, { name: 'Face Swap', value: 15, color: '#8b5cf6' },
  { name: 'Voice Clone', value: 12, color: '#f59e0b' }, { name: 'Movie Shorts', value: 9, color: '#06b6d4' },
  { name: 'Travel', value: 10, color: '#10b981' }, { name: 'Political', value: 8, color: '#ef4444' },
  { name: 'Kids', value: 7, color: '#ec4899' }, { name: 'Propaganda', value: 6, color: '#84cc16' },
  { name: 'Education', value: 6, color: '#14b8a6' }, { name: 'Adult', value: 5, color: '#6b7280' },
  { name: 'Other', value: 4, color: '#64748b' },
];
const MOCK_PLATFORM_ORIGIN: PlatformOrigin[] = [
  { platform: 'Twitter / X', count: 1240, percentage: 28, icon: MessageCircle, color: '#1da1f2' },
  { platform: 'Instagram', count: 980, percentage: 22, icon: Camera, color: '#e1306c' },
  { platform: 'YouTube', count: 850, percentage: 19, icon: Video, color: '#ff0000' },
  { platform: 'TikTok', count: 720, percentage: 16, icon: Music, color: '#000000' },
  { platform: 'Facebook', count: 410, percentage: 9, icon: Share2, color: '#1877f2' },
  { platform: 'Snapchat', count: 260, percentage: 6, icon: Camera, color: '#fffc00' },
];
const MOCK_COUNTRY_ORIGIN: CountryOrigin[] = [
  { country: 'United States', percentage: 29 }, { country: 'China', percentage: 27 },
  { country: 'United Kingdom', percentage: 12 }, { country: 'Germany', percentage: 9 },
  { country: 'India', percentage: 8 }, { country: 'Brazil', percentage: 6 },
  { country: 'Japan', percentage: 5 }, { country: 'Australia', percentage: 4 },
];
const MOCK_TAG_ADOPTION: TagAdoption[] = [
  { tag: '#DeepFake', count: 48, growthRate: 4.8, suggestedCategory: 'Entertainment' },
  { tag: '#travelvlog', count: 77, growthRate: 1.2, suggestedCategory: 'Travel' },
  { tag: '#political', count: 26, growthRate: -1.5, suggestedCategory: 'Political' },
  { tag: '#cooking', count: 29, growthRate: 2.3, suggestedCategory: 'Food' },
  { tag: '#Propaganda', count: 24, growthRate: 1.8, suggestedCategory: 'Travel' },
  { tag: '#coalint', count: 10, growthRate: 0.5, suggestedCategory: 'Politics' },
  { tag: '#foodie', count: 7, growthRate: 1.1, suggestedCategory: 'Food' },
  { tag: '#digitalart', count: 1, growthRate: 0.2, suggestedCategory: 'Entertainment' },
];
const MOCK_PROVIDER_STATUS: ProviderStatus[] = [
  { name: 'VMiE Ai', responseTime: 89, successRate: 95, errors: 0, status: 'operational' },
  { name: 'Mistral', responseTime: 75, successRate: 33, errors: 1, status: 'operational' },
  { name: 'OpenAI', responseTime: 75, successRate: 23, errors: 0, status: 'operational' },
];
const MOCK_UPLOAD_PATTERNS = [
  { hour: 0, weekday: 25, weekend: 15 }, { hour: 4, weekday: 18, weekend: 12 },
  { hour: 8, weekday: 45, weekend: 35 }, { hour: 12, weekday: 52, weekend: 48 },
  { hour: 16, weekday: 48, weekend: 52 }, { hour: 20, weekday: 35, weekend: 42 }, { hour: 24, weekday: 28, weekend: 22 },
];
const MOCK_CATEGORY_TREND = [
  { time: '00:00', value: 120 }, { time: '04:00', value: 95 }, { time: '08:00', value: 180 },
  { time: '12:00', value: 250 }, { time: '16:00', value: 220 }, { time: '20:00', value: 190 }, { time: '24:00', value: 150 },
];
const MOCK_QUEUE_DEPTH = [
  { time: '11:00', depth: 8 }, { time: '12:00', depth: 12 }, { time: '13:00', depth: 15 },
  { time: '14:00', depth: 10 }, { time: '15:00', depth: 13 }, { time: '16:00', depth: 11 },
];
const MOCK_ERROR_RATE = [
  { time: '11:00', rate: 1.2 }, { time: '12:00', rate: 2.5 }, { time: '13:00', rate: 1.8 },
  { time: '14:00', rate: 1.5 }, { time: '15:00', rate: 2.1 }, { time: '16:00', rate: 1.6 },
];

// ─────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────────────
const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};
const getRiskColor = (level: string) => {
  switch (level) {
    case 'low': return 'text-emerald-600 dark:text-emerald-400';
    case 'medium': return 'text-yellow-600 dark:text-yellow-400';
    case 'high': return 'text-red-600 dark:text-red-400';
    default: return 'text-gray-400';
  }
};
const getRiskBgColor = (level: string) => {
  switch (level) {
    case 'low': return 'bg-emerald-500';
    case 'medium': return 'bg-yellow-500';
    case 'high': return 'bg-red-500';
    default: return 'bg-gray-500';
  }
};

// ─────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────
interface RetractableCardProps {
  title: string;
  icon: React.ElementType;
  iconColor?: string;
  items: any[];
  renderItem: (item: any, index: number) => React.ReactNode;
  defaultVisible?: number;
}
const RetractableCard: React.FC<RetractableCardProps> = ({
  title, icon: Icon, iconColor = 'text-blue-500', items, renderItem, defaultVisible = 3
}) => {
  const [expanded, setExpanded] = useState(false);
  const visibleItems = expanded ? items : items.slice(0, defaultVisible);
  const hasMore = items.length > defaultVisible;
  return (
    <div className="bg-white/10 dark:bg-gray-900/80 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-xl p-3 shadow-lg flex flex-col max-h-[220px]">
      <div className="flex items-center gap-2 mb-2 shrink-0">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">{title}</h3>
      </div>
      <div className="space-y-1.5 overflow-y-auto custom-scrollbar pr-1 flex-1">
        {visibleItems.map(renderItem)}
      </div>
      {hasMore && (
        <button onClick={() => setExpanded(!expanded)} className="mt-2 flex items-center justify-center gap-1 text-[10px] text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors shrink-0">
          {expanded ? <><ChevronUp className="w-3 h-3" /> Show less</> : <><ChevronDown className="w-3 h-3" /> Show {items.length - defaultVisible} more</>}
        </button>
      )}
    </div>
  );
};

interface GaugeProps { value: number; max?: number; label: string; sublabel?: string; color?: string; size?: 'sm' | 'md' | 'lg'; }
const Gauge: React.FC<GaugeProps> = ({ value, max = 100, label, sublabel, color = '#10b981', size = 'md' }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const radius = size === 'sm' ? 30 : size === 'md' ? 40 : 50;
  const strokeWidth = size === 'sm' ? 6 : size === 'md' ? 8 : 10;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const dimensions = { sm: { width: 80, height: 80 }, md: { width: 100, height: 100 }, lg: { width: 120, height: 120 } };
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={dimensions[size]}>
        <svg className="transform -rotate-90" width={dimensions[size].width} height={dimensions[size].height}>
          <circle cx={dimensions[size].width / 2} cy={dimensions[size].height / 2} r={radius} stroke="#e5e7eb" strokeWidth={strokeWidth} fill="none" className="dark:stroke-gray-700" />
          <circle cx={dimensions[size].width / 2} cy={dimensions[size].height / 2} r={radius} stroke={color} strokeWidth={strokeWidth} fill="none" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" className="transition-all duration-500" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-${size === 'sm' ? 'lg' : size === 'md' ? 'xl' : '2xl'} font-bold text-gray-900 dark:text-white`}>{Math.round(percentage)}%</span>
        </div>
      </div>
      <p className="text-sm font-medium text-gray-900 dark:text-white mt-2">{label}</p>
      {sublabel && <p className="text-xs text-gray-600 dark:text-gray-400">{sublabel}</p>}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────
const IntelTelemetry: React.FC = () => {
  const { user, isLoaded } = useUser();
  const { isAdminAuthenticated: _isAdminAuthenticated } = useAdminAuth();
  const navigate = useNavigate();
  
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasValidLicense, setHasValidLicense] = useState(false);
  
  // Admin Login Modal State
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<ActiveTab>('commandMap');
  const [contentSubTab, setContentSubTab] = useState<ContentSubTab>('tools');
  const [behaviorSubTab, setBehaviorSubTab] = useState<BehaviorSubTab>('patterns');
  const [regionFilter, setRegionFilter] = useState<RegionFilter>('global');
  const [timeRange, setTimeRange] = useState<TimeRange>('5m');
  const [isLive, setIsLive] = useState(true);
  const [stats, setStats] = useState<TelemetryStats>(MOCK_STATS);
  const [geoData] = useState<GeoData[]>(MOCK_GEO_DATA);
  const [activityFeed, setActivityFeed] = useState<ActivityEvent[]>(() => {
    const userName = user?.fullName || user?.emailAddresses[0]?.emailAddress || 'Admin User';
    return [
      { id: '1', timestamp: new Date().toLocaleTimeString(), type: 'upload', description: `${userName} initiated system audit`, countryCode: 'US' },
      { id: '2', timestamp: new Date(Date.now() - 60000).toLocaleTimeString(), type: 'alert', description: 'Risk alert: Inappropriate content detected', riskLevel: 'high', countryCode: 'CN' },
      { id: '3', timestamp: new Date(Date.now() - 120000).toLocaleTimeString(), type: 'milestone', description: 'Global milestone: 10,000th analysis completed', countryCode: 'GLOBAL' },
    ];
  });
  
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number; data: GeoData | null }>({
    visible: false, x: 0, y: 0, data: null,
  });
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [feedEnabled, setFeedEnabled] = useState(true);

  useEffect(() => {
    if (isLoaded) checkAdminAccess();
  }, [isLoaded, user]);

  useEffect(() => {
    if (isAdmin || hasValidLicense) {
      setIsLoading(false);
      const interval = setInterval(() => {
        if (isLive && feedEnabled) updateStats();
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isAdmin, hasValidLicense, isLive, feedEnabled]);

  const checkAdminAccess = () => {
    const userMetadata = user?.publicMetadata as { role?: string; license?: string } | undefined;
    
    // 1. Check if user is admin via Clerk metadata
    if (userMetadata?.role === 'admin') {
      setIsAdmin(true);
      return;
    }
    
    // 2. Check if user has a valid business/enterprise license
    const licenseTier = userMetadata?.license || 'free';
    if (licenseTier === 'business' || licenseTier === 'enterprise') {
      setHasValidLicense(true);
      return;
    }
    
    // 3. If neither, show the interactive login modal
    setShowLoginModal(true);
  };

  const handleAdminLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (adminEmail === ADMIN_CREDENTIALS.email && adminPassword === ADMIN_CREDENTIALS.password) {
      setIsAdmin(true);
      setShowLoginModal(false);
      setAdminEmail('');
      setAdminPassword('');
    } else {
      setLoginError('Invalid admin credentials');
    }
  };

  const handleLogout = () => {
    setIsAdmin(false);
    setHasValidLicense(false);
    setShowLoginModal(true);
  };

  const updateStats = () => {
    setStats(prev => ({
      ProcessedMedia: prev.ProcessedMedia + Math.floor(Math.random() * 10) - 5,
      maxProcessingTime: prev.maxProcessingTime + Math.floor(Math.random() * 20) - 10,
      avgProcessingTime: Math.max(1.5, prev.avgProcessingTime + (Math.random() * 0.4 - 0.2)),
      riskAlerts: prev.riskAlerts + (Math.random() > 0.7 ? 1 : 0),
    }));
    if (Math.random() > 0.7) {
      const userName = user?.fullName || user?.emailAddresses[0]?.emailAddress || 'Admin User';
      const newEvent: ActivityEvent = {
        id: Date.now().toString(), timestamp: new Date().toLocaleTimeString('en-US', { hour12: false }),
        type: Math.random() > 0.5 ? 'upload' : 'alert',
        description: Math.random() > 0.5 ? `${userName} uploaded new media file (Trust: 0.91)` : 'Risk alert: Inappropriate content detected',
        riskLevel: Math.random() > 0.7 ? 'high' : 'medium', countryCode: ['US', 'GB', 'DE', 'JP', 'CN'][Math.floor(Math.random() * 5)],
      };
      setActivityFeed(prev => [newEvent, ...prev.slice(0, 20)]);
    }
  };

  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current?.key === key) return current.direction === 'asc' ? { key, direction: 'desc' } : null;
      return { key, direction: 'asc' };
    });
  };

  const getSortedData = <T extends Record<string, any>>(data: T[], key: string) => {
    if (!sortConfig || sortConfig.key !== key) return data;
    return [...data].sort((a, b) => {
      if (a[key] < b[key]) return sortConfig.direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // ───────────────────────────────────────────────────────────────────
  // RENDER - Admin Login Modal
  // ───────────────────────────────────────────────────────────────────
  if (!isAdmin && !hasValidLicense && showLoginModal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Admin Access Required
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              This page contains sensitive telemetry data. Enter admin credentials or upgrade your license.
            </p>
          </div>

          <form onSubmit={handleAdminLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Admin Email
              </label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder="*****@*****"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {loginError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{loginError}</p>
              </div>
            )}

            <button
              type="submit"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <Lock className="w-5 h-5" />
              Authenticate
            </button>

            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="w-full px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Back to Dashboard
            </button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-xs text-blue-800 dark:text-blue-300">
              <strong>Demo Credentials:</strong><br />
              Email: admin@<br />
              Password: ********** 
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center"><div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="text-gray-600 dark:text-gray-400">Loading telemetry data...</p></div>
    </div>
  );

  return (
    <div className="pt-[80px] px-4 sm:px-6 lg:px-8 pb-8 max-w-7xl mx-auto w-full min-h-screen bg-gray-50 dark:bg-gray-900">
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
        .dark .custom-scrollbar::-webkit-scrollbar-track { background: #374151; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #6b7280; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div><h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Real-Time Analytics</h1><p className="text-gray-600 dark:text-gray-400 text-sm">Live intelligence & telemetry monitoring</p></div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-300 dark:border-emerald-700 rounded-lg shrink-0"><div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" /><span className="text-emerald-700 dark:text-emerald-400 text-sm font-medium">Live</span></div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded-lg shrink-0"><Shield className="w-4 h-4 text-blue-600 dark:text-blue-400" /><span className="text-blue-700 dark:text-blue-400 text-sm font-medium">Admin</span></div>
          <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shrink-0"><LogOut className="w-4 h-4" /><span className="text-sm font-medium">Exit Admin</span></button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between mb-4"><div className="min-w-0 flex-1"><p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1 truncate">Total Media Processed
          </p><p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">{formatNumber(stats.ProcessedMedia)}</p></div><div className={`p-2 sm:p-3 rounded-lg shrink-0 ml-2 ${THEME.emerald.bg}`}><Users className={`w-5 h-5 sm:w-6 sm:h-6 ${THEME.emerald.text}`} /></div></div>
          <div className="flex items-center gap-2 text-xs sm:text-sm"><div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" /><span className="text-emerald-600 dark:text-emerald-400 font-medium">Live</span></div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between mb-4"><div className="min-w-0 flex-1"><p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1 truncate">Max Ingestion Latency /ms</p><p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">{stats.maxProcessingTime}ms</p></div><div className={`p-2 sm:p-3 rounded-lg shrink-0 ml-2 ${THEME.blue.bg}`}><Zap className={`w-5 h-5 sm:w-6 sm:h-6 ${THEME.blue.text}`} /></div></div>
          <p className="text-xs text-gray-500 dark:text-gray-500">Real time</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between mb-4"><div className="min-w-0 flex-1"><p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1 truncate">Avg Processing Time</p><p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">{stats.avgProcessingTime.toFixed(1)}ms</p></div><div className={`p-2 sm:p-3 rounded-lg shrink-0 ml-2 ${THEME.purple.bg}`}><Clock className={`w-5 h-5 sm:w-6 sm:h-6 ${THEME.purple.text}`} /></div></div>
          <p className="text-xs text-gray-500 dark:text-gray-500">Live</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between mb-4"><div className="min-w-0 flex-1"><p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1 truncate">Risk Alerts (Last Hour)</p><p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">{stats.riskAlerts}</p></div><div className={`p-2 sm:p-3 rounded-lg shrink-0 ml-2 ${THEME.amber.bg}`}><AlertTriangle className={`w-5 h-5 sm:w-6 sm:h-6 ${THEME.amber.text}`} /></div></div>
          <span className="inline-flex px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded">Inappropriate content</span>
        </div>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6 mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6 overflow-x-auto hide-scrollbar">
          <nav className="flex whitespace-nowrap gap-2 sm:gap-6">
            {[
              { id: 'commandMap', label: 'Live Command Map', icon: Globe },
              { id: 'content', label: 'Content Origin Telemetry', icon: Activity },
              { id: 'behavior', label: 'User Behavior & Trends', icon: TrendingUp },
              { id: 'system', label: 'System Health & Performance', icon: Server },
            ].map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id as ActiveTab)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}>
                <tab.icon className="w-4 h-4" />{tab.label}
              </button>
            ))}
          </nav>
        </div>
        <div>
          {/* TAB 1: LIVE COMMAND MAP */}
          {activeTab === 'commandMap' && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2"><div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" /><span className="text-gray-900 dark:text-white text-sm font-medium">Live</span></div>
                  <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    <button onClick={() => setRegionFilter('global')} className={`px-3 py-1 rounded text-xs font-medium transition-colors ${regionFilter === 'global' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'}`}>Global</button>
                    <button onClick={() => setRegionFilter('region')} className={`px-3 py-1 rounded text-xs font-medium transition-colors ${regionFilter === 'region' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'}`}>Region</button>
                  </div>
                  <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    {(['5m', '15m', '1h', '24h'] as TimeRange[]).map((range) => (
                      <button key={range} onClick={() => setTimeRange(range)} className={`px-3 py-1 rounded text-xs font-medium transition-colors ${timeRange === range ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'}`}>{range}</button>
                    ))}
                  </div>
                </div>
                <button onClick={updateStats} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-xs font-medium transition-colors"><RefreshCw className="w-3 h-3" /> Refresh</button>
              </div>
              <div
                ref={mapContainerRef}
                className="relative h-[500px] sm:h-[600px] bg-gray-100 dark:bg-gray-950 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 mb-4"
                onMouseLeave={() => setTooltip(prev => ({ ...prev, visible: false }))}
              >
                <ComposableMap projectionConfig={{ scale: 165, center: [10, 15] }} className="w-full h-full">
                  <Geographies geography={worldData}>
                    {({ geographies }) =>
                      geographies.map((geo) => {
                        const countryName = geo.properties.name;
                        const countryData = MOCK_GEO_DATA.find(g => g.country === countryName);
                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill={countryData ? (countryData.riskLevel === 'high' ? '#ef444440' : countryData.riskLevel === 'medium' ? '#f59e0b40' : '#10b98140') : '#2a324d'}
                            stroke="#2a324d"
                            strokeWidth={0.5}
                            style={{
                              default: { outline: 'none' },
                              hover: {
                                outline: 'none',
                                fill: countryData ? '#ffffff25' : '#3a425d',
                                cursor: countryData ? 'pointer' : 'default',
                              },
                              pressed: { outline: 'none' },
                            }}
                            onMouseEnter={(evt) => {
                              if (!countryData || !mapContainerRef.current) return;
                              const rect = mapContainerRef.current.getBoundingClientRect();
                              setTooltip({
                                visible: true,
                                x: evt.clientX - rect.left,
                                y: evt.clientY - rect.top,
                                data: countryData,
                              });
                            }}
                            onMouseMove={(evt) => {
                              if (!countryData || !mapContainerRef.current) return;
                              const rect = mapContainerRef.current.getBoundingClientRect();
                              setTooltip(prev => ({
                                ...prev,
                                x: evt.clientX - rect.left,
                                y: evt.clientY - rect.top,
                              }));
                            }}
                            onMouseLeave={() => {
                              setTooltip(prev => ({ ...prev, visible: false }));
                            }}
                          />
                        );
                      })
                    }
                  </Geographies>
                </ComposableMap>

                {tooltip.visible && tooltip.data && (() => {
                  const containerRect = mapContainerRef.current?.getBoundingClientRect();
                  const tooltipWidth = 180;
                  const tooltipHeight = 130;
                  const flipX = containerRect && (tooltip.x + tooltipWidth + 20 > containerRect.width);
                  const flipY = containerRect && (tooltip.y + tooltipHeight + 20 > containerRect.height);
                  return (
                    <div
                      className="absolute pointer-events-none z-50 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-xl border border-gray-700 min-w-[160px] transition-opacity duration-100"
                      style={{
                        left: flipX ? `${tooltip.x - tooltipWidth - 12}px` : `${tooltip.x + 12}px`,
                        top: flipY ? `${tooltip.y - tooltipHeight - 12}px` : `${tooltip.y + 12}px`,
                      }}
                    >
                      <p className="font-bold text-sm mb-1 text-blue-400">{tooltip.data.country}</p>
                      <p className="text-gray-300">Uploads: <span className="text-white font-semibold">{tooltip.data.uploads}</span></p>
                      <p className="text-gray-300">Trust: <span className="text-white font-semibold">{(tooltip.data.avgTrustScore * 100).toFixed(0)}%</span></p>
                      <p className={`font-semibold capitalize ${getRiskColor(tooltip.data.riskLevel)}`}>Risk: {tooltip.data.riskLevel}</p>
                      <div className="mt-1 pt-1 border-t border-gray-700 flex justify-between gap-3 text-[10px]">
                        <span className="text-purple-400">AI: {tooltip.data.aiGenerated}%</span>
                        <span className="text-emerald-400">Human: {tooltip.data.humanCreated}%</span>
                      </div>
                    </div>
                  );
                })()}

                <div className="absolute top-3 left-3 w-56 sm:w-64 z-30">
                  <RetractableCard title="Top Countries" icon={Globe} iconColor="text-blue-500" items={[...geoData].sort((a, b) => b.uploads - a.uploads)} defaultVisible={3}
                    renderItem={(item, idx) => (<div key={idx} className="flex items-center justify-between text-xs"><span className="text-gray-800 dark:text-gray-200 font-medium truncate">{item.country}</span><span className="text-blue-500 font-semibold">{item.uploads}</span></div>)} />
                </div>
                <div className="absolute top-3 right-3 w-56 sm:w-64 z-30">
                  <RetractableCard title="Top Tools" icon={BarChart3} iconColor="text-emerald-500" items={MOCK_CREATOR_TOOLS} defaultVisible={3}
                    renderItem={(item, idx) => { const Icon = item.icon || Box; return (<div key={idx} className="flex items-center justify-between text-xs"><div className="flex items-center gap-2 min-w-0"><Icon className="w-3 h-3 text-gray-400 shrink-0" /><span className="text-gray-800 dark:text-gray-200 font-medium truncate">{item.tool}</span></div><span className="text-emerald-500 font-semibold shrink-0 ml-2">{formatNumber(item.uploadCount)}</span></div>); }} />
                </div>
                <div className="absolute bottom-3 left-3 w-56 sm:w-64 z-30">
                  <RetractableCard title="Content Types" icon={PieChartIcon} iconColor="text-purple-500" items={MOCK_CONTENT_TYPES} defaultVisible={3}
                    renderItem={(item, idx) => (<div key={idx} className="flex items-center justify-between text-xs"><div className="flex items-center gap-2 min-w-0"><div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.color }} /><span className="text-gray-800 dark:text-gray-200 font-medium truncate">{item.name}</span></div><span className="text-gray-500 dark:text-gray-400 font-semibold shrink-0 ml-2">{item.value}%</span></div>)} />
                </div>
                <div className="absolute bottom-3 right-3 w-56 sm:w-64 z-30">
                  <RetractableCard title="Top Platforms" icon={Share2} iconColor="text-amber-500" items={MOCK_PLATFORM_ORIGIN} defaultVisible={3}
                    renderItem={(item, idx) => { const Icon = item.icon; return (<div key={idx} className="flex items-center justify-between text-xs"><div className="flex items-center gap-2 min-w-0"><Icon className="w-3 h-3 shrink-0" style={{ color: item.color }} /><span className="text-gray-800 dark:text-gray-200 font-medium truncate">{item.platform}</span></div><span className="text-amber-500 font-semibold shrink-0 ml-2">{item.percentage}%</span></div>); }} />
                </div>
              </div>
            </div>
          )}
          {/* TAB 2: CONTENT ORIGIN TELEMETRY */}
          {activeTab === 'content' && (
            <div>
              <div className="flex gap-4 mb-6 border-b border-gray-200 dark:border-gray-700 pb-2 overflow-x-auto hide-scrollbar">
                <button onClick={() => setContentSubTab('tools')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${contentSubTab === 'tools' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}><BarChart3 className="w-4 h-4" /> Leaderboard</button>
                <button onClick={() => setContentSubTab('distribution')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${contentSubTab === 'distribution' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}><PieChartIcon className="w-4 h-4" /> Analytics</button>
                <button onClick={() => setContentSubTab('platform')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${contentSubTab === 'platform' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}><Share2 className="w-4 h-4" /> Platform Origin</button>
              </div>
              {contentSubTab === 'tools' && (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400 cursor-pointer hover:text-blue-500" onClick={() => handleSort('rank')}>Rank <ArrowUpDown className="inline w-3 h-3 ml-1" /></th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Tool/Platform</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400 cursor-pointer hover:text-blue-500" onClick={() => handleSort('uploadCount')}>Upload Count <ArrowUpDown className="inline w-3 h-3 ml-1" /></th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400 cursor-pointer hover:text-blue-500" onClick={() => handleSort('avgTrust')}>Avg Trust <ArrowUpDown className="inline w-3 h-3 ml-1" /></th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400 cursor-pointer hover:text-blue-500" onClick={() => handleSort('riskPercent')}>Risk % <ArrowUpDown className="inline w-3 h-3 ml-1" /></th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {getSortedData(MOCK_CREATOR_TOOLS, 'rank').map((tool) => {
                        const Icon = tool.icon || Box;
                        return (<tr key={tool.rank} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{tool.rank}</td>
                          <td className="py-3 px-4"><div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center"><Icon className="w-4 h-4 text-gray-600 dark:text-gray-400" /></div><span className="text-sm font-medium text-gray-900 dark:text-white">{tool.tool}</span></div></td>
                          <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">{formatNumber(tool.uploadCount)}</td>
                          <td className="py-3 px-4 text-sm text-emerald-600 dark:text-emerald-400">{tool.avgTrust.toFixed(2)}</td>
                          <td className="py-3 px-4"><span className={`text-sm font-medium ${tool.riskPercent < 20 ? 'text-emerald-600 dark:text-emerald-400' : tool.riskPercent < 50 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>{tool.riskPercent}%</span></td>
                        </tr>);
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {contentSubTab === 'distribution' && (
                <div className="flex flex-col lg:flex-row gap-6">
                  <div className="flex-1"><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Content Type Distribution</h3>
                    <div className="h-64 mb-6"><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={MOCK_CONTENT_TYPES} cx="50%" cy="50%" innerRadius={60} outerRadius={90} fill="#8884d8" dataKey="value">{MOCK_CONTENT_TYPES.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} />))}</Pie><Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#111827' }} /></PieChart></ResponsiveContainer></div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">{MOCK_CONTENT_TYPES.map((type, idx) => (<div key={idx} className="flex items-center gap-2"><div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: type.color }} /><span className="text-gray-700 dark:text-gray-300 truncate">{type.name}</span><span className="text-gray-500 dark:text-gray-400">{type.value}%</span></div>))}</div>
                  </div>
                  <div className="flex-1"><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Category Trend over Last 24h</h3>
                    <div className="h-48 sm:h-64 mb-6"><ResponsiveContainer width="100%" height="100%"><AreaChart data={MOCK_CATEGORY_TREND}><defs><linearGradient id="colorTrend" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/><stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/></linearGradient></defs><CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" /><XAxis dataKey="time" stroke="#6b7280" fontSize={12} /><YAxis stroke="#6b7280" fontSize={12} /><Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#111827' }} /><Area type="monotone" dataKey="value" stroke="#3b82f6" fillOpacity={1} fill="url(#colorTrend)" /></AreaChart></ResponsiveContainer></div>
                  </div>
                </div>
              )}
              {contentSubTab === 'platform' && (
                <div className="max-w-3xl">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Platform Origin Distribution</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[500px]">
                      <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400 cursor-pointer hover:text-blue-500" onClick={() => handleSort('platform')}>Platform <ArrowUpDown className="inline w-3 h-3 ml-1" /></th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400 cursor-pointer hover:text-blue-500" onClick={() => handleSort('count')}>Uploads <ArrowUpDown className="inline w-3 h-3 ml-1" /></th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400 cursor-pointer hover:text-blue-500" onClick={() => handleSort('percentage')}>Share % <ArrowUpDown className="inline w-3 h-3 ml-1" /></th>
                      </tr></thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {getSortedData(MOCK_PLATFORM_ORIGIN, 'percentage').map((item, idx) => {
                          const Icon = item.icon;
                          return (<tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="py-3 px-4"><div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center"><Icon className="w-4 h-4" style={{ color: item.color }} /></div><span className="text-sm font-medium text-gray-900 dark:text-white">{item.platform}</span></div></td>
                            <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">{formatNumber(item.count)}</td>
                            <td className="py-3 px-4"><div className="flex items-center gap-2"><div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${item.percentage}%`, backgroundColor: item.color }} /></div><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{item.percentage}%</span></div></td>
                          </tr>);
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* TAB 3: USER BEHAVIOR & TRENDS */}
          {activeTab === 'behavior' && (
            <div>
              <div className="flex gap-4 mb-6 border-b border-gray-200 dark:border-gray-700 pb-2 overflow-x-auto hide-scrollbar">
                <button onClick={() => setBehaviorSubTab('countryOrigin')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${behaviorSubTab === 'countryOrigin' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}><Globe className="w-4 h-4" /> Country Origin</button>
                <button onClick={() => setBehaviorSubTab('patterns')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${behaviorSubTab === 'patterns' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}><BarChart3 className="w-4 h-4" /> Upload Patterns</button>
                <button onClick={() => setBehaviorSubTab('tags')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${behaviorSubTab === 'tags' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}><Tag className="w-4 h-4" /> Tag Adoption</button>
              </div>
              {behaviorSubTab === 'patterns' && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 sm:p-6 w-full">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">Upload Patterns & Analytics</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-4 sm:space-y-6 w-full min-w-0">
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-600 w-full overflow-hidden"><h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Upload Patterns Heatmap</h4>
                        <div className="overflow-x-auto custom-scrollbar pb-2"><div className="min-w-[260px]">
                          <div className="grid grid-cols-8 gap-1 mb-1">{['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb'].map((m, i) => (<div key={i} className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 text-center truncate">{m}</div>))}</div>
                          <div className="grid grid-cols-8 gap-1">{[...Array(48)].map((_, i) => (<div key={i} className={`aspect-square sm:aspect-auto sm:h-3 rounded-[2px] ${Math.random() > 0.5 ? 'bg-emerald-500' : 'bg-emerald-900'}`} style={{ opacity: 0.3 + Math.random() * 0.7 }} />))}</div>
                          <div className="flex justify-between text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-2 px-1"><span>Real</span><span>UTC</span></div>
                        </div></div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-600 w-full"><h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Weekday vs Weekend Trends</h4>
                        <div className="h-48 w-full"><ResponsiveContainer width="100%" height="100%"><LineChart data={MOCK_UPLOAD_PATTERNS}><CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" /><XAxis dataKey="hour" stroke="#6b7280" fontSize={11} /><YAxis stroke="#6b7280" fontSize={11} /><Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#111827' }} /><Legend iconSize={8} wrapperStyle={{ fontSize: '12px' }} /><Line type="monotone" dataKey="weekday" stroke="#3b82f6" strokeWidth={2} dot={false} name="Weekday" /><Line type="monotone" dataKey="weekend" stroke="#10b981" strokeWidth={2} dot={false} name="Weekend" /></LineChart></ResponsiveContainer></div>
                      </div>
                    </div>
                    <div className="space-y-4 sm:space-y-6 w-full min-w-0">
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-600 w-full"><h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Key Insights</h4>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600"><span className="text-gray-600 dark:text-gray-400">Peak Time</span><span className="text-gray-900 dark:text-white font-medium">2-4 PM UTC</span></div>
                          <div className="flex items-center justify-between text-xs p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600"><span className="text-gray-600 dark:text-gray-400">Weekend Trend</span><span className="text-emerald-600 dark:text-emerald-400 font-medium">+34% Uploads</span></div>
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 sm:p-8 border border-gray-200 dark:border-gray-600 flex flex-col items-center justify-center min-h-[200px] w-full text-center"><Upload className="w-12 h-12 text-gray-400 dark:text-gray-500 opacity-30 mb-3" /><h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Advanced Analytics</h4><p className="text-gray-500 dark:text-gray-400 text-xs max-w-[250px]">Geographic breakdowns and device metrics coming soon.</p></div>
                    </div>
                  </div>
                </div>
              )}
              {behaviorSubTab === 'countryOrigin' && (
                <div className="max-w-3xl">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Country of Origin Distribution</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[500px]">
                      <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400 cursor-pointer hover:text-blue-500" onClick={() => handleSort('country')}>Country <ArrowUpDown className="inline w-3 h-3 ml-1" /></th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400 cursor-pointer hover:text-blue-500" onClick={() => handleSort('percentage')}>Upload Share % <ArrowUpDown className="inline w-3 h-3 ml-1" /></th>
                      </tr></thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {getSortedData(MOCK_COUNTRY_ORIGIN, 'percentage').map((item, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="py-3 px-4"><div className="flex items-center gap-3"><Globe className="w-5 h-5 text-blue-500" /><span className="text-sm font-medium text-gray-900 dark:text-white">{item.country}</span></div></td>
                            <td className="py-3 px-4"><div className="flex items-center gap-2"><div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${item.percentage}%` }} /></div><span className="text-sm font-bold text-gray-900 dark:text-white w-12 text-right">{item.percentage}%</span></div></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {behaviorSubTab === 'tags' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div><h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Tag Adoption</h4>
                    <div className="space-y-2 max-h-96 overflow-y-auto custom-scrollbar pr-2">{MOCK_TAG_ADOPTION.map((t, i) => (
                      <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg gap-2">
                        <div className="flex-1"><p className="text-sm font-medium text-gray-900 dark:text-white">{t.tag}</p><div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400"><span>Count: {t.count}</span><span className={t.growthRate >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>{t.growthRate >= 0 ? '+' : ''}{t.growthRate}%</span></div></div>
                        <button className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors shrink-0">Suggest Category</button>
                      </div>
                    ))}</div>
                  </div>
                  <div><h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Crowdsourcing Info</h4>
                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"><p className="text-sm text-gray-600 dark:text-gray-400 mb-4">For admins: Map user tags to official categories for better content classification.</p>
                      <div className="space-y-3"><div className="flex items-center justify-between text-sm"><span className="text-gray-600 dark:text-gray-400">Confidence Score</span><div className="flex items-center gap-2"><div className="w-24 h-2 bg-gray-300 dark:bg-gray-600 rounded-full overflow-hidden"><div className="w-3/4 h-full bg-emerald-500" /></div><span className="text-emerald-600 dark:text-emerald-400 font-medium">75%</span></div></div></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* TAB 4: SYSTEM HEALTH */}
          {activeTab === 'system' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-start justify-between mb-4"><div className="min-w-0 flex-1"><p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1 truncate">Queue Depth</p><p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">12</p></div><div className={`p-2 sm:p-3 rounded-lg shrink-0 ml-2 ${THEME.blue.bg}`}><Activity className={`w-5 h-5 sm:w-6 sm:h-6 ${THEME.blue.text}`} /></div></div>
                  <div className="h-24 mb-4"><ResponsiveContainer width="100%" height="100%"><AreaChart data={MOCK_QUEUE_DEPTH}><defs><linearGradient id="colorQueue" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/><stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/></linearGradient></defs><Area type="monotone" dataKey="depth" stroke="#3b82f6" fillOpacity={1} fill="url(#colorQueue)" /></AreaChart></ResponsiveContainer></div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Current queue size</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-start justify-between mb-4"><div className="min-w-0 flex-1"><p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1 truncate">Error Rate</p><p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">1.8%</p></div><div className={`p-2 sm:p-3 rounded-lg shrink-0 ml-2 ${THEME.amber.bg}`}><AlertTriangle className={`w-5 h-5 sm:w-6 sm:h-6 ${THEME.amber.text}`} /></div></div>
                  <div className="h-24 mb-4"><ResponsiveContainer width="100%" height="100%"><LineChart data={MOCK_ERROR_RATE}><Line type="monotone" dataKey="rate" stroke="#ef4444" strokeWidth={2} dot={false} /></LineChart></ResponsiveContainer></div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Last 6 hours average</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-start justify-between mb-4"><div className="min-w-0 flex-1"><p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1 truncate">Resource Usage</p></div><div className={`p-2 sm:p-3 rounded-lg shrink-0 ml-2 ${THEME.purple.bg}`}><Cpu className={`w-5 h-5 sm:w-6 sm:h-6 ${THEME.purple.text}`} /></div></div>
                  <div className="flex justify-around py-4"><Gauge value={18} label="CPU" color="#3b82f6" size="sm" /><Gauge value={27} label="Memory" color="#8b5cf6" size="sm" /><Gauge value={33} label="Disk" color="#10b981" size="sm" /></div>
                </div>
              </div>
              <div><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">AI Engine Performance</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">{MOCK_PROVIDER_STATUS.map((p) => (
                  <div key={p.name} className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4"><h4 className="font-semibold text-gray-900 dark:text-white">{p.name}</h4><span className={`text-xs font-medium px-2 py-1 rounded-full ${p.status === 'operational' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : p.status === 'degraded' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>{p.status}</span></div>
                    <div className="flex justify-center mb-4"><Gauge value={p.successRate} label="Success Rate" color={p.successRate >= 95 ? '#10b981' : p.successRate >= 80 ? '#f59e0b' : '#ef4444'} size="md" /></div>
                    <div className="grid grid-cols-2 gap-4 text-sm"><div><p className="text-gray-600 dark:text-gray-400 text-xs">Response Time</p><p className="font-semibold text-gray-900 dark:text-white">{p.responseTime}ms</p></div><div><p className="text-gray-600 dark:text-gray-400 text-xs">Errors</p><p className="font-semibold text-gray-900 dark:text-white">{p.errors}</p></div></div>
                  </div>
                ))}</div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-semibold text-gray-900 dark:text-white">Live Activity Feed</h3>
          <button onClick={() => setFeedEnabled(!feedEnabled)} className={`w-12 h-6 rounded-full transition-colors ${feedEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}><div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${feedEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} /></button>
        </div>
        <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar pr-2">
          {activityFeed.slice(0, 8).map((e) => (
            <div key={e.id} className="flex items-start gap-3 text-xs sm:text-sm p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <span className="text-gray-500 dark:text-gray-400 font-mono shrink-0">[{e.timestamp}]</span>
              <span className={`flex-1 ${e.type === 'alert' ? 'text-red-600 dark:text-red-400' : e.type === 'milestone' ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-900 dark:text-gray-300'}`}>{e.description}</span>
              {e.type === 'upload' && <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />}
              {e.type === 'alert' && <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 shrink-0" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IntelTelemetry;
